import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";

import {
    PostHSPlanStatusEditResource,
    PostHSPlanStatusViewResource,
    Permission,
    PostHighSchoolStatus
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { PostHSPlanStatusPageHeader } from "./PostHSPlanHeader";
import { PostHSPlanStatusPageBody } from "./PostHSPlanStatusPageBody";

const routeName = "postHSPlanStatus";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const postHSPlanStatusPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Post-HS Status",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type PostHSPlanStatusContextValue = {
    onCreate?: (id: number) => void;
};

export const PostHSPlanStatusContext = createContext<PostHSPlanStatusContextValue>({} as PostHSPlanStatusContextValue);

export const PostHSPlanStatusPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);

    const [postHSPlanStatus, setPostHSPlanStatus] = useState<PostHighSchoolStatus>({} as PostHighSchoolStatus);
    const [initPostHSPlanStatus, setInitPostHSPlanStatus] = useState<PostHighSchoolStatus>(postHSPlanStatus);
    const [viewResource, setViewResource] = useState<PostHSPlanStatusViewResource>({});
    const [editResource, setEditResource] = useState<PostHSPlanStatusEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [postHSPlanStatus] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.postHSPlanStatus.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.postHSPlanStatus.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${postHSPlanStatusPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.postHSPlanStatus.get(id).then((response) => {
                setPostHSPlanStatus(response);
                setInitPostHSPlanStatus(response);
                resetUnsavedChanges([response]);
            });
        } else {
            // For new
            setPostHSPlanStatus({} as PostHighSchoolStatus);
            setInitPostHSPlanStatus({} as PostHighSchoolStatus);
            resetUnsavedChanges([{} as PostHighSchoolStatus]);
        }
    };

    const pageHeader = (
        <PostHSPlanStatusPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={postHSPlanStatusPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            postHSPlanStatus={postHSPlanStatus}
            setPostHSPlanStatus={setPostHSPlanStatus}
            initPostHSPlanStatus={initPostHSPlanStatus}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <PostHSPlanStatusPageBody
            postHSPlanStatus={postHSPlanStatus}
            setPostHSPlanStatus={setPostHSPlanStatus}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};